import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '../utils/apiClient';
import { getFormattedWeight } from '../utils/productFormatting';

interface LineItem {
  lineItemId: string;
  productName: string;
  productNameOriginal: string;
  rawLineText: string;
  quantity: number;
  unitPriceFormatted: string;
  totalPriceFormatted: string;
  dateFormatted: string;
}

interface Receipt {
  receiptId: string;
  orderNumber: string;
  orderDate: number;
  dateFormatted: string;
  store: string;
  storeLocation?: string;
  totalGrossFormatted: string;
  totalNetFormatted: string;
  totalDiscountFormatted: string;
  lineItemCount: number;
}

interface OrderDetailsResponse {
  receipt: Receipt;
  lineItems: LineItem[];
  totalLineItems: number;
}

export default function OrderDetailsPage() {
  const { receiptId } = useParams<{ receiptId: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<OrderDetailsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!receiptId) return;

    const fetchOrderDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await apiClient.get(`/api/receipts?receiptId=${receiptId}`);
        
        setData(response.data);
      } catch (err) {
        console.error('Failed to fetch order details:', err);
        setError('Failed to load order details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [receiptId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-red-700">
        <h2 className="text-lg font-bold mb-2">Error</h2>
        <p>{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-yellow-700">
        <p>No order details found.</p>
      </div>
    );
  }

  const { receipt, lineItems } = data;

  return (
    <div className="page-shell">
      <div className="flex flex-col gap-3">
        <button onClick={() => navigate('/receipts')} className="btn-ghost w-max">← Back to orders</button>
        <div>
          <h1 className="text-3xl font-semibold text-ink-700">Order {receipt.orderNumber}</h1>
          <p className="text-sm text-ink-500 mt-1">{receipt.dateFormatted}</p>
        </div>
      </div>

      <section className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <div className="card p-6">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">Total (gross)</p>
          <p className="mt-3 text-3xl font-semibold text-ink-700">{receipt.totalGrossFormatted}</p>
        </div>
        <div className="card p-6">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">Total (net)</p>
          <p className="mt-3 text-3xl font-semibold text-ink-700">{receipt.totalNetFormatted}</p>
        </div>
        <div className="card p-6">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">Discount</p>
          <p className="mt-3 text-3xl font-semibold text-primary-600">{receipt.totalDiscountFormatted}</p>
        </div>
        <div className="card p-6">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">Items</p>
          <p className="mt-3 text-3xl font-semibold text-ink-700">{receipt.lineItemCount}</p>
        </div>
      </section>

      <section className="card p-6">
        <h2 className="text-xl font-semibold text-ink-700">Store information</h2>
        <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <p className="text-xs uppercase tracking-wide text-ink-400">Store</p>
            <p className="mt-2 text-sm font-semibold text-ink-700">
              {receipt.store.charAt(0).toUpperCase() + receipt.store.slice(1)}
            </p>
          </div>
          {receipt.storeLocation && (
            <div>
              <p className="text-xs uppercase tracking-wide text-ink-400">Location</p>
              <p className="mt-2 text-sm font-semibold text-ink-700">{receipt.storeLocation}</p>
            </div>
          )}
        </div>
      </section>

      <section className="card overflow-hidden">
        <div className="border-b border-ink-100/70 p-6">
          <h2 className="text-xl font-semibold text-ink-700">Items ({lineItems.length})</h2>
        </div>
        <div className="table-card border-transparent">
          <table className="table-default">
            <thead>
              <tr>
                <th>Product</th>
                <th className="text-center">Quantity</th>
                <th className="text-right">Unit price</th>
                <th className="text-right">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100/80">
              {lineItems.map((item) => {
                const weightFormatted = getFormattedWeight(item.rawLineText);

                return (
                  <tr key={item.lineItemId}>
                    <td className="font-medium text-ink-700">
                      <button
                        onClick={() => {
                          const params = new URLSearchParams();
                          params.append('product', item.productName);
                          navigate(`/price-history?${params.toString()}`);
                        }}
                        className="text-left text-accent-600 underline decoration-dotted underline-offset-4 transition hover:text-accent-500"
                      >
                        {item.productNameOriginal}
                      </button>
                      {weightFormatted && (
                        <div className="text-xs text-ink-400 mt-1">{weightFormatted}</div>
                      )}
                    </td>
                    <td className="text-center text-ink-500">{item.quantity}</td>
                    <td className="text-right font-medium text-ink-600">{item.unitPriceFormatted}</td>
                    <td className="text-right font-semibold text-ink-700">{item.totalPriceFormatted}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={3} className="text-right text-ink-500">Gross total</td>
                <td className="text-right text-lg font-semibold text-ink-700">{receipt.totalGrossFormatted}</td>
              </tr>
              <tr>
                <td colSpan={3} className="text-right text-ink-500">Discount</td>
                <td className="text-right text-lg font-semibold text-primary-600">-{receipt.totalDiscountFormatted}</td>
              </tr>
              <tr>
                <td colSpan={3} className="text-right text-ink-500">Net total</td>
                <td className="text-right text-lg font-semibold text-accent-600">{receipt.totalNetFormatted}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>
    </div>
  );
}
